/**
 * Data Types Modal Component
 *
 * Modal for creating and editing data types.
 */

import { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import RefreshButton from '@/components/common/RefreshButton';
import type { DataType } from '@/types/associations';

interface DataTypesModalProps {
  show: boolean;
  dataType?: DataType | null;
  onHide: () => void;
  onSave: (data: Partial<DataType>) => Promise<void>;
  onRefresh?: () => Promise<void>;
}

export default function DataTypesModal({ show, dataType, onHide, onSave, onRefresh }: DataTypesModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (dataType) {
      setFormData({
        name: dataType.name || '',
        description: dataType.description || '',
      });
    } else {
      setFormData({
        name: '',
        description: '',
      });
    }
    setError(null);
  }, [dataType, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await onSave(formData);
      onHide();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.policyDatasets.saveFailed', { type: 'data type' }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered scrollable>
      <Modal.Header closeButton>
        <div className="d-flex justify-content-between align-items-center w-100 me-3">
          <Modal.Title>{dataType ? t('admin.policyDatasets.editDataType') : t('admin.policyDatasets.createDataType')}</Modal.Title>
          {onRefresh && <RefreshButton onRefresh={onRefresh} size="sm" variant="outline-secondary" />}
        </div>
      </Modal.Header>
      <Modal.Body>
        <Form id="datatypes-form" onSubmit={handleSubmit}>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>
              {t('admin.policyDatasets.name')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('admin.policyDatasets.namePlaceholder', { example: 'ECG' })}
              required
              autoFocus
            />
            <Form.Text className="text-muted">{t('admin.policyDatasets.nameRequired', { type: 'data type' })}</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('admin.policyDatasets.description')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('admin.policyDatasets.descriptionPlaceholder')}
            />
            <Form.Text className="text-muted">{t('admin.policyDatasets.descriptionOptional')}</Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" type="submit" form="datatypes-form" disabled={isSaving}>
          {isSaving ? t('admin.policyDatasets.saving') : dataType ? t('common.update') : t('common.create')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
